import { apiClient } from '@app/api';
import { ApiPlayerDataProvider, SegmentsAudioPlayer, Station, TimeRange } from '@mi-front/core';
import { PropsWithChildren, useContext, useEffect, useMemo } from 'react';
import { PlaybackStoreContext } from '../context/playback.context';
import { PlaybackProvider } from '../context/PlaybackProvider';
import { ExclusivePlaybackMode } from '../store/base-playback-slice';
import { createAudioPlaybackStore } from './useAudioPlaybackStore';

export type SegmentsAudioPlaybackProviderProps = {
  station: Station;
  /** Optional playback range for bounded playback (e.g., fragment chunks) */
  playbackRange?: TimeRange<number>;
  /** Optional source URL for media */
  src?: string;
  exclusivePlaybackMode?: ExclusivePlaybackMode;
  /** Optional identifier for this playback instance, shown in debug logs */
  playbackId?: string;
};

/**
 * Provider component that creates a SegmentsAudioPlayer and passes it to the audio playback store.
 * Used for segmented audio playback (e.g., logger audio mode with playback ranges).
 */
export function SegmentsAudioPlaybackProvider({
  station,
  playbackRange,
  src,
  exclusivePlaybackMode = 'pause',
  playbackId,
  children,
}: PropsWithChildren<SegmentsAudioPlaybackProviderProps>) {
  // Create store exactly once per component instance
  const store = useMemo(
    () =>
      createAudioPlaybackStore({
        player: new SegmentsAudioPlayer(
          new ApiPlayerDataProvider(station.id, apiClient.segments),
          playbackId ?? station.slug,
        ),
        src,
        playbackRange,
        exclusivePlaybackMode,
        playbackId: playbackId ?? station.slug,
      }),
    [],
  );

  // When nested inside another playback context (e.g. logger stream), register this store with the parent
  const ctx = useContext(PlaybackStoreContext)!;

  useEffect(() => {
    ctx?.setStore(store);
  }, []);

  // Root usage: no parent context — provide our own PlaybackProvider
  if (!ctx) {
    return <PlaybackProvider store={store}>{children}</PlaybackProvider>;
  }

  // Nested usage: render only when parent context points to this store instance.
  return ctx.store === store ? children : null;
}
